<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container">
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          @click="$emit('close')"
          aria-label="Close 3D model info"
        >
          ✕
        </button>
        <h3>3D model upload complete!</h3>
      </div>
      
      <div class="modal-body">
        <!-- Success Animation -->
        <div class="success-animation">
          <div class="success-checkmark">
            <div class="checkmark-circle">
              <div class="checkmark-stem"></div>
              <div class="checkmark-kick"></div>
            </div>
          </div>
          <div class="upload-icon">📦</div>
        </div>
        
        <!-- Main Content -->
        <div class="content">
          <h3>Your 3D model is under review</h3>
          <p class="main-message">
            Thank you for uploading your GLB file! Our team will review your 3D model to ensure it meets our quality standards and campaign requirements.
          </p>
          
          <div class="placeholder-info">
            <div class="info-icon">📧</div>
            <h4>Email notification coming soon</h4>
            <p>
              You'll receive an email notification once your GLB asset has been approved and is ready to use in your campaign. This typically takes 1-2 business days.
            </p>
          </div>
          
          <!-- Review Process Timeline -->
          <div class="review-timeline">
            <div class="timeline-item completed">
              <div class="timeline-icon">✅</div>
              <div class="timeline-content">
                <h4>Upload complete</h4>
                <p>Your GLB file has been successfully uploaded</p>
              </div>
            </div>
            
            <div class="timeline-item in-progress">
              <div class="timeline-icon">🔍</div>
              <div class="timeline-content">
                <h4>Quality review</h4>
                <p>Our team is reviewing your 3D model for technical compatibility</p>
              </div>
            </div>
            
            <div class="timeline-item pending">
              <div class="timeline-icon">🎯</div>
              <div class="timeline-content">
                <h4>Campaign integration</h4>
                <p>Once approved, your model will be integrated into your campaign</p>
              </div>
            </div>
          </div>
          
          <!-- Temporary Placeholder Section -->
          <div class="placeholder-section">
            <h4>Temporary 3D preview</h4>
            <p>While we review your model, you'll see this placeholder in your campaign:</p>
            
            <div class="placeholder-preview">
              <div class="placeholder-image">
                <img 
                  src="../assets/logo.svg" 
                  alt="Temporary 3D placeholder" 
                  class="temp-preview-image"
                />
                <div class="placeholder-overlay">
                  <span class="placeholder-text">Temporary 3D Model</span>
                </div>
              </div>
              <p class="preview-caption">
                This placeholder will be replaced with your GLB model once the review is complete.
              </p>
            </div>
          </div>
          
          <!-- Review Information -->
          <div class="review-info">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-icon">⏱️</span>
                <div>
                  <strong>Review Time</strong>
                  <p>Typically 2-4 business days</p>
                </div>
              </div>
              
              <div class="info-item">
                <span class="info-icon">📧</span>
                <div>
                  <strong>Notification</strong>
                  <p>You'll receive an email when review is complete</p>
                </div>
              </div>
              
              <div class="info-item">
                <span class="info-icon">🔄</span>
                <div>
                  <strong>Updates</strong>
                  <p>Check your dashboard for status updates</p>
                </div>
              </div>
              
              <div class="info-item">
                <span class="info-icon">❓</span>
                <div>
                  <strong>Questions</strong>
                  <p>Contact support if you need assistance</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- What happens next -->
          <div class="next-steps">
            <h4>What happens next?</h4>
            <ol>
              <li>Your campaign will use the temporary placeholder while we review your GLB model</li>
              <li>Our team will check for technical requirements and compatibility</li>
              <li>Once approved, your actual 3D model will replace the placeholder</li>
              <li>You'll receive confirmation and can preview the final result</li>
            </ol>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="action-buttons">
          <button @click="$emit('close')" class="primary-button">
            Continue Building
          </button>
          <button @click="handleContactSupport" class="secondary-button">
            Support
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'GLBInfoModal',
  emits: ['close'],
  setup(_, { emit }) {
    const handleOverlayClick = (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        emit('close')
      }
    }
    
    const handleContactSupport = () => {
      // This could open a support modal, redirect to support page, etc.
      window.open('mailto:support@example.com?subject=3D Model Upload Question', '_blank')
    }
    
    return {
      handleOverlayClick,
      handleContactSupport
    }
  }
})
</script>

<style scoped>
.modal-overlay {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.success-animation {
  position: relative;
  margin-bottom: 30px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-checkmark {
  position: relative;
  width: 80px;
  height: 80px;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid var(--vt-c-accent);
  background-color: rgba(var(--vt-c-accent-rgb), 0.1);
  position: relative;
  animation: circle-fill 0.6s ease-in-out forwards;
}

.checkmark-stem {
  position: absolute;
  width: 3px;
  height: 20px;
  background-color: var(--vt-c-accent);
  left: 35px;
  top: 35px;
  transform: rotate(45deg);
  animation: checkmark-stem 0.3s ease-in-out 0.6s both;
}

.checkmark-kick {
  position: absolute;
  width: 3px;
  height: 12px;
  background-color: var(--vt-c-accent);
  left: 25px;
  top: 43px;
  transform: rotate(-45deg);
  animation: checkmark-kick 0.3s ease-in-out 0.9s both;
}

.upload-icon {
  position: absolute;
  font-size: 1.5rem;
  color: var(--vt-c-accent);
  z-index: 1;
  top: 10px;
  right: 10px;
}

@keyframes circle-fill {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes checkmark-stem {
  0% {
    height: 0;
  }
  100% {
    height: 20px;
  }
}

@keyframes checkmark-kick {
  0% {
    height: 0;
  }
  100% {
    height: 12px;
  }
}

.content h3 {
  margin: 0 0 15px 0;
  color: var(--color-heading);
  font-size: 1.4rem;
  font-weight: 600;
  text-align: center;
}

.main-message {
  margin: 0 0 30px 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.6;
  text-align: center;
}

.review-timeline {
  margin: 30px 0;
  text-align: left;
}

.timeline-item {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: flex-start;
}

.timeline-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  min-width: 40px;
}

.timeline-item.completed .timeline-icon {
  background-color: rgba(74, 222, 128, 0.2);
  border: 2px solid #4ade80;
}

.timeline-item.in-progress .timeline-icon {
  background-color: rgba(var(--vt-c-accent-rgb), 0.2);
  border: 2px solid var(--vt-c-accent);
}

.timeline-item.pending .timeline-icon {
  background-color: var(--color-background-mute);
  border: 2px solid var(--color-border);
}

.timeline-content h4 {
  margin: 0 0 5px 0;
  color: var(--color-heading);
  font-size: 1rem;
  font-weight: 600;
}

.timeline-content p {
  margin: 0;
  color: var(--color-text);
  font-size: 0.875rem;
  line-height: 1.4;
}

.placeholder-section {
  margin: 40px 0;
  padding: 25px;
  background-color: var(--color-background-soft);
  border-radius: 12px;
  border-left: 4px solid var(--vt-c-accent);
}

.placeholder-section h4 {
  margin: 0 0 10px 0;
  color: var(--color-heading);
  font-size: 1.2rem;
  font-weight: 600;
}

.placeholder-section > p {
  margin: 0 0 20px 0;
  color: var(--color-text);
  font-size: 0.95rem;
}

.placeholder-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.placeholder-image {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--color-border);
  background-color: var(--color-background-mute);
}

.temp-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.7;
}

.placeholder-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-text {
  color: var(--ui-inverse-control-text);
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  padding: 5px;
  font-family: var(--ui-inverse-font-family);
}

.preview-caption {
  margin: 0;
  color: var(--vt-c-text-muted);
  font-size: 0.875rem;
  text-align: center;
  font-style: italic;
}

.review-info {
  margin: 30px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.info-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  text-align: left;
}

.info-icon {
  font-size: 1.5rem;
  min-width: 30px;
}

.info-item strong {
  display: block;
  color: var(--color-heading);
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.info-item p {
  margin: 0;
  color: var(--color-text);
  font-size: 0.875rem;
  line-height: 1.4;
}

.next-steps {
  margin: 30px 0;
  text-align: left;
  padding: 20px;
  background-color: rgba(var(--vt-c-accent-rgb), 0.1);
  border-radius: 8px;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.3);
}

.next-steps h4 {
  margin: 0 0 15px 0;
  color: var(--color-heading);
  font-size: 1.1rem;
  font-weight: 600;
}

.next-steps ol {
  margin: 0;
  padding-left: 20px;
  color: var(--color-text);
}

.next-steps li {
  margin-bottom: 8px;
  font-size: 0.9rem;
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  width: 100%;
}

.primary-button {
  background: linear-gradient(135deg, var(--vt-c-accent), #4ade80);
  color: var(--color-background);
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  flex: 1;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--vt-c-accent-rgb), 0.3);
}

.secondary-button {
  background: transparent;
  color: var(--color-text);
  border: 2px solid var(--color-border);
  padding: 12px 26px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
}

.secondary-button:hover {
  border-color: var(--vt-c-accent);
  color: var(--vt-c-accent);
}

.placeholder-info {
  background: linear-gradient(135deg, rgba(255, 243, 205, 0.1) 0%, rgba(255, 234, 167, 0.1) 100%);
  border: 1px solid #f39c12;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

.placeholder-info .info-icon {
  font-size: 2rem;
  margin-bottom: 10px;
  display: block;
}

.placeholder-info h4 {
  color: var(--color-heading);
  margin: 0 0 10px 0;
  font-size: 1.1rem;
}

.placeholder-info p {
  color: var(--color-text);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media (max-width: 700px) {
  .info-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .primary-button,
  .secondary-button {
    width: 100%;
  }
}
</style>
